import { useRequiredContext } from "@redotech/react-util/context";
import { createContext } from "react";

// Product routing interface
export interface MerchantRoutes {
  // Home routes
  overviewHref: string;
  homeBaseHref: string;
  analyticsHref: string;
  getAnalyticsSubRoute: (subPath: string) => string;

  // Shipping routes
  shippingDefaultRoute: string;
  ordersOutboundLabelsHref: string;
  getOrdersOutboundLabelsSubRoute: (subPath: string) => string;
  pickSessionHref: string;
  pickSessionTableHref: string;
  transferHref: string;
  instantTransferHref: string;
  receiveToBinHref: string;
  consolidationHref: string;
  consolidationsHref: string;
  flushHref: string;
  warehouseHref: string;
  warehouseBaseUrl: string;
  orderEditsHref: string;
  analyticsOrderFulfillmentHref: string;
  getAnalyticsOrderFulfillmentSubRoute: (subPath: string) => string;
  shippingCarrierClaimsHref: string;

  // Returns routes
  returnsDefaultRoute: string;
  returnsHref: string;
  getReturnsSubRoute: (subPath: string) => string;
  claimsHref: string;
  claimsDefaultRoute: string;
  getClaimsSubRoute: (subPath: string) => string;

  getWarrantiesHref: ({ enabled }: { enabled: boolean }) => string;
  warrantiesHref: string;
  getWarrantiesSubRoute: (subPath: string) => string;

  registrationsHref: string;
  returnsProcessingHref: string;

  getRecycleHref: () => string;
  recycleHref: string;
  getRecycleSubRoute: (subPath: string) => string;

  getWithdrawalHref: () => string;
  withdrawalHref: string;
  getWithdrawalSubRoute: (subPath: string) => string;

  fraudHref: string;
  getFraudSubRoute: (subPath: string) => string;

  analyticsReturnsClaimsHref: string;
  getAnalyticsReturnsClaimsSubRoute: (subPath: string) => string;
  analyticsReportsHref: string;
  analyticsManagedReturnsHref: string;
  managedReturnsHref: string;
  getManagedReturnsSubRoute: (subPath: string) => string;
  warehouseSlasHref: string;
  inboundDashboardHref: string;
  processingPipelineHref: string;
  qaMetricsHref: string;
  refulfillmentHref: string;

  // Inventory routes
  getInventoryHref: ({ enabled }: { enabled: boolean }) => string;
  inventoryProductsHref: string;
  inventoryAnalyticsReportsHref: string;

  // Marketing routes
  marketingAnalyticsHref: string;
  marketingAnalyticsReportsHref: string;
  automationsHref: string;
  marketingPopupsHref: string;
  getMarketingPopupsSubRoute: (subPath: string) => string;
  getSegmentBuilderRoute({
    teamId,
    segmentId,
  }: {
    teamId: string;
    segmentId: string;
  }): string;
  getTrackingHref: ({ enabled }: { enabled: boolean }) => string;
  getTrackingSubRoute: (subPath: string) => string;
  recoverHref: string;
  getRecoverSubRoute: (subPath: string) => string;
  contextualHref: string;
  getContextualSubRoute: (subPath: string) => string;
  reviewsHref: string;
  getReviewsSubRoute: (subPath: string) => string;
  emailSmsHref: string;
  getEmailSmsSubRoute: (subPath: string) => string;
  discountsHref: string;
  getDiscountsSubRoute: (subPath: string) => string;
  loyaltyHref: string;
  getLoyaltySubRoute: (subPath: string) => string;
  marketingAgentHref: string;
  getMarketingAgentSubRoute: (subPath: string) => string;
  shopperAgentHref: string;
  getShopperAgentSubRoute: (subPath: string) => string;
  textConciergeHref: string;
  getTextConciergeSubRoute: (subPath: string) => string;
  getMarketingHref: ({
    emailSmsEnabled,
    marketingCloudEnabled,
  }: {
    emailSmsEnabled: boolean;
    marketingCloudEnabled: boolean;
  }) => string;

  // Support routes
  getSupportHref: ({ enabled }: { enabled: boolean }) => string;
  getConversationHref: (conversationId: string) => string;
  getConciergeHref: ({ enabled }: { enabled: boolean }) => string;
  getConciergeSubRoute: (subPath: string) => string;
  getFaqArticleEditorHref: (faqArticleId: string) => string;

  // Conversion routes
  popupsHref: string;
  getConversionPopupsSubRoute: (subPath: string) => string;
  /** @deprecated Prefer `popupsHref`. */
  spotlightsHref: string;
  /** @deprecated Prefer `getPopupsSubRoute`. */
  getSpotlightsSubRoute: (subPath: string) => string;
  conversionAnalyticsHref: string;
  conversionAnalyticsReportsHref: string;
  getConversionHref: ({
    enabled,
    checkoutOutboundRatesEnabled,
  }: {
    enabled: boolean;
    checkoutOutboundRatesEnabled: boolean;
  }) => string;
  getConversionSubRoute: (subPath: string) => string;

  // Customers routes
  getCustomerRecordHref: ({ customerId }: { customerId?: string }) => string;

  // Settings routes
  settingsHref: string;
  getSettingsSubRoute: (subPath: string) => string;

  // Base URLs for sections
  shippingBaseUrl: string;
  returnsBaseUrl: string;
  inventoryManagementBaseUrl: string;
  catalogBaseUrl: string;
  catalogProductsHref: string;
  marketingBaseUrl: string;
  // Always `/support`; retained only for the deprecated v1 support redirect
  // targets and backwards-compatible bookmarks.
  legacySupportBaseUrl: string;
  // Explicit Support V2 root for routes that only exist in the new app.
  supportV2BaseUrl: string;
  // Flag-selected support entry point; points at `/support-v2` when v2 is enabled.
  supportBaseUrl: string;
  // Home for the FAQ / help-center pages (`/support-v2/faq`).
  faqBaseUrl: string;
  conversionBaseUrl: string;
  customersBaseUrl: string;
  storeBaseUrl: string;
  aeoBaseUrl: string;

  // Payments routes
  paymentsBaseUrl: string;
  chargebacksHref: string;
  chargebacksOverviewHref: string;
  chargebacksReclaimHref: string;
  chargebacksAlertsHref: string;
  chargebacksResolveHref: string;
  paymentsHsaHref: string;
  paymentsHsaQuestionnaireHref: string;
  paymentsHsaTransactionsHref: string;
  paymentsHsaPayoutsHref: string;

  // Redo Supplies routes
  redoSuppliesBaseUrl: string;
  redoSuppliesShopHref: string;
  redoSuppliesOrderHistoryHref: string;
  redoSuppliesRecurringPurchasesInventoryHref: string;
  redoSuppliesRecurringPurchasesTimeHref: string;

  // AEO routes
  aeoDashboardHref: string;
  aeoIntegrationsHref: string;
  aeoPromptsHref: string;
  aeoCatalogHref: string;
  aeoAssistantHref: string;
  aeoCompetitorsHref: string;
  aeoBrandHref: string;
  getAeoSubRoute: (subPath: string) => string;
  getAeoCatalogSubRoute: (subPath: string) => string;

  // Developer portal routes
  developerBaseUrl: string;
  developerFunctionsHref: string;
  developerLogsHref: string;
}

export const MerchantRoutesContext = createContext<MerchantRoutes | undefined>(
  undefined,
);

export function useMerchantRoutes(): MerchantRoutes {
  return useRequiredContext(MerchantRoutesContext);
}
